import { useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  LinearProgress,
  List,
  ListItemButton,
  ListItemText,
  TextField,
  Typography,
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import type { SmartSearchResult } from '@finance/contracts';
import { getSmartSearchBrowseDescriptor } from '@finance/contracts/experience/smart-search-browse';
import { translateRuntimeTKey, resolveTKey } from '../../i18n/resolve-key';
import { formatValue, createFormatDescriptor } from '../../runtime/formatting';
import { recordSmartSearchSelection } from './smart-search.api';
import {
  rememberSearchDestination,
  resolveSearchDestination,
  useCommandPalette,
} from './use-command-palette';

interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
}

function humanizeCode(value: string): string {
  return value
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .toLowerCase()
    .replace(/^./, (letter) => letter.toUpperCase());
}

export function CommandPalette({ open, onClose }: CommandPaletteProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const lastActiveElementRef = useRef<HTMLElement | null>(null);
  const selectionStateRef = useRef<{ query: string; results: SmartSearchResult[]; partial: boolean }>({
    query: '',
    results: [],
    partial: false,
  });

  const translateRuntimeCode = useCallback((key: string, fallback: string) => {
    return translateRuntimeTKey(t, key) ?? fallback;
  }, [t]);

  const handleClose = useCallback(() => {
    onClose();
    window.requestAnimationFrame(() => lastActiveElementRef.current?.focus());
  }, [onClose]);

  const handleSelect = useCallback(
    (result: SmartSearchResult) => {
      const destination = resolveSearchDestination(result);
      if (!destination || !result.routeId) return;
      const selection = selectionStateRef.current;
      const rank = Math.max(0, selection.results.findIndex((candidate) => candidate.id === result.id));
      rememberSearchDestination(result);
      void recordSmartSearchSelection({
        resultKind: result.kind,
        entityType: result.entityType,
        routeId: result.routeId,
        rank,
        queryLength: Array.from(selection.query.trim()).length,
        partial: selection.partial,
      }).catch(() => {
        // Selection telemetry is non-blocking and must never prevent navigation.
      });
      handleClose();
      navigate(destination);
    },
    [navigate, handleClose],
  );

  const {
    query,
    setQuery,
    activeIndex,
    setActiveIndex,
    results,
    grouped,
    loading,
    refreshing,
    error,
    partial,
    stale,
    retry,
    listboxId,
  } = useCommandPalette({ open, onSelect: handleSelect, onClose: handleClose });

  selectionStateRef.current = { query, results, partial };

  useEffect(() => {
    if (!open) return;
    lastActiveElementRef.current = document.activeElement as HTMLElement | null;
  }, [open]);

  useEffect(() => {
    if (!open) return;
    window.requestAnimationFrame(() => inputRef.current?.focus());
  }, [open]);

  useEffect(() => {
    if (!open || results.length === 0) return;
    listRef.current?.querySelector(`[data-cmd-index="${activeIndex}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex, results.length, open]);

  const activeResult = results[activeIndex];
  const activeDescendantId = activeResult ? `command-${activeResult.id}` : undefined;
  const queryMode = query.trim().length > 0;

  const clearSearch = useCallback(() => {
    setQuery('');
    setActiveIndex(0);
    window.requestAnimationFrame(() => inputRef.current?.focus());
  }, [setActiveIndex, setQuery]);

  const renderLabel = (result: SmartSearchResult): string =>
    result.labelKey ? t(resolveTKey(result.labelKey)) : result.title;

  const renderDescription = (result: SmartSearchResult): string | undefined => {
    if (result.descriptionKey) return t(resolveTKey(result.descriptionKey));
    if (result.entityType === 'TRANSACTION' && result.statusKey && result.dateIso) {
      const translatedStatus = t(resolveTKey(result.statusKey));
      const parsed = new Date(result.dateIso);
      if (!Number.isNaN(parsed.getTime())) {
        return `${translatedStatus} · ${formatValue(parsed, createFormatDescriptor('date'))}`;
      }
      return translatedStatus;
    }
    return result.subtitle;
  };

  let runningIndex = 0;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      aria-labelledby="command-palette-title"
      slotProps={{
        paper: {
          sx: {
            mt: { xs: 1, sm: 4 },
            border: 1,
            borderColor: 'divider',
            overflow: 'hidden',
          },
        },
      }}
    >
      <DialogTitle
        id="command-palette-title"
        sx={{ pb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}
      >
        <Typography component="span" variant="h6">
          {t('shell:commandPalette.dialogAria')}
        </Typography>
        <IconButton
          aria-label={t('common:close')}
          onClick={handleClose}
          size="small"
          sx={{ minWidth: 40, minHeight: 40 }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ px: { xs: 1.5, sm: 2.5 }, pb: 2.5 }}>
        <TextField
          fullWidth
          inputRef={inputRef}
          value={query}
          onChange={(event) => {
            setQuery(event.target.value);
            setActiveIndex(0);
          }}
          placeholder={t('shell:commandPalette.searchPlaceholder')}
          aria-controls={listboxId}
          aria-activedescendant={activeDescendantId}
          slotProps={{
            htmlInput: {
              role: 'combobox',
              'aria-autocomplete': 'list',
              'aria-expanded': true,
              'aria-haspopup': 'listbox',
              'aria-describedby': 'command-palette-status',
            },
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: queryMode ? (
                <InputAdornment position="end">
                  <IconButton
                    aria-label={t('common:clear')}
                    edge="end"
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={clearSearch}
                    size="small"
                    sx={{ minWidth: 40, minHeight: 40 }}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : undefined,
            },
          }}
          sx={{ mb: 0.5 }}
          data-testid="command-palette-search"
        />

        <Box sx={{ height: 3, mb: 1.5 }} aria-hidden="true">
          {refreshing && <LinearProgress sx={{ height: 2 }} data-testid="command-palette-refreshing" />}
        </Box>

        {loading && results.length === 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }} data-testid="command-palette-loading">
            <CircularProgress size={28} />
          </Box>
        )}

        {!loading && error && results.length === 0 && (
          <Alert
            severity="error"
            data-testid="command-palette-error"
            action={<Button color="inherit" size="small" onClick={retry}>{t('shell:commandPalette.retry')}</Button>}
          >
            {t('shell:userMenu.navigationRequestFailed')}
          </Alert>
        )}

        {!loading && stale && results.length > 0 && (
          <Alert
            severity="warning"
            data-testid="command-palette-stale-results"
            sx={{ mb: 1 }}
            action={<Button color="inherit" size="small" onClick={retry}>{t('shell:commandPalette.retry')}</Button>}
          >
            {t('shell:commandPalette.staleResults')}
          </Alert>
        )}

        {!loading && !stale && partial && (
          <Alert severity="warning" data-testid="command-palette-partial-results" sx={{ mb: 1 }}>
            {t('common:states.degraded.message')}
          </Alert>
        )}

        <Typography
          id="command-palette-status"
          role="status"
          aria-live="polite"
          aria-atomic="true"
          sx={{
            position: 'absolute',
            width: '1px',
            height: '1px',
            overflow: 'hidden',
            clip: 'rect(0 0 0 0)',
          }}
        >
          {loading ? t('common:loading') : `${results.length} ${t('shell:commandPalette.dialogAria')}`}
        </Typography>

        {results.length > 0 && (
          <Box
            id={listboxId}
            ref={listRef}
            role="listbox"
            aria-busy={refreshing}
            sx={{ maxHeight: { xs: '62vh', sm: '56vh' }, overflowY: 'auto' }}
          >
            {grouped.map((group, groupIndex) => {
              const categoryLabel = translateRuntimeCode(
                `shell.commandPalette.categories.${group.category}`,
                humanizeCode(String(group.category)),
              );
              return (
                <Box key={group.key} role="group" aria-label={categoryLabel}>
                  <Typography
                    variant="overline"
                    sx={{ px: 1.5, pt: groupIndex === 0 ? 0 : 1, display: 'block', color: 'text.secondary' }}
                  >
                    {categoryLabel}
                  </Typography>
                  <List component="div" role="presentation" disablePadding>
                    {group.items.map((result) => {
                      const index = runningIndex++;
                      const entityLabel = result.entityLabelKey ? t(resolveTKey(result.entityLabelKey)) : undefined;
                      const browseGroup = result.browseGroup
                        ?? (result.routeId ? getSmartSearchBrowseDescriptor(String(result.routeId))?.group : undefined);
                      const browseContextLabel = browseGroup
                        ? translateRuntimeCode(
                            `shell.commandPalette.categories.${browseGroup.toLowerCase()}`,
                            humanizeCode(browseGroup),
                          )
                        : undefined;
                      const kindLabel = translateRuntimeCode(
                        `shell.commandPalette.resultKinds.${result.kind.toLowerCase()}`,
                        humanizeCode(result.kind),
                      );
                      const contextLabel = entityLabel ?? browseContextLabel ?? kindLabel;

                      return (
                        <ListItemButton
                          id={`command-${result.id}`}
                          key={result.id}
                          role="option"
                          tabIndex={-1}
                          aria-selected={index === activeIndex}
                          data-cmd-index={index}
                          selected={index === activeIndex}
                          onClick={() => handleSelect(result)}
                          onMouseEnter={() => setActiveIndex(index)}
                          sx={{ borderRadius: 1.5, mx: 0.5, px: 1.5, py: 1 }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, width: '100%', minWidth: 0 }}>
                            <ListItemText
                              primary={renderLabel(result)}
                              secondary={renderDescription(result)}
                              sx={{ minWidth: 0 }}
                              slotProps={{
                                primary: { sx: { fontWeight: 600, fontSize: '0.9rem' } },
                                secondary: { noWrap: true, sx: { fontSize: '0.75rem' } },
                              }}
                            />
                            {(contextLabel || queryMode) && (
                              <Chip
                                label={contextLabel}
                                size="small"
                                variant="outlined"
                                sx={{ flexShrink: 0 }}
                              />
                            )}
                          </Box>
                        </ListItemButton>
                      );
                    })}
                  </List>
                  {groupIndex < grouped.length - 1 && <Divider sx={{ my: 0.75 }} />}
                </Box>
              );
            })}
            {!queryMode && (
              <Typography variant="caption" sx={{ display: 'block', px: 2, py: 1.5, color: 'text.disabled' }}>
                {t('shell:commandPalette.browseHint')}
              </Typography>
            )}
            {queryMode && results.length >= 24 && (
              <Typography variant="caption" sx={{ display: 'block', px: 2, py: 1, color: 'text.disabled', textAlign: 'center' }}>
                {t('shell:commandPalette.resultLimitHint')}
              </Typography>
            )}
          </Box>
        )}

        {!loading && !error && results.length === 0 && (
          <Typography sx={{ px: 2, py: 6, textAlign: 'center', color: 'text.secondary' }}>
            {queryMode ? t('shell:commandPalette.noMatches') : t('shell:commandPalette.noDestinations')}
          </Typography>
        )}
      </DialogContent>
    </Dialog>
  );
}
